/**
 * Fashion Design Studio Model
 *
 * Comprehensive fashion design studio leveraging:
 * - FLUX Krea: Photorealistic fashion imagery (text-to-image)
 * - FLUX Kontext: Context-aware editing, clothing swaps, BEST for sketch-to-image
 * - FLUX General: Multi-control support (hedsketch for sketch inputs)
 * - Image animation capabilities
 * - Niche agent integration for prompt improvement
 *
 * Sketch-to-Image Recommendations:
 * 1. flux-kontext: Best quality for fashion sketches with context understanding
 * 2. flux-general: Fast alternative with dedicated hedsketch control
 * 3. sdxl-controlnet-canny: Legacy fallback for speed-critical scenarios
 */

export interface FashionDesignSession {
  sessionId: string;
  userId: string;
  projectName: string;
  designPhase: DesignPhase;
  createdAt: Date;
  updatedAt: Date;
  designs: FashionDesign[];
  moodBoard?: MoodBoardItem[];
  settings: FashionStudioSettings;
}

export type DesignPhase =
  | 'concept'           // Initial sketches and concepts
  | 'sketch-to-image'   // Convert sketches to realistic renders
  | 'styling'           // Apply different contexts and styles
  | 'animation'         // Animate the fashion piece (✅ Validated)
  | '360-view'          // Generate multi-angle views (✅ Validated)
  | 'virtual-tryon'     // Virtual try-on with specialized models
  | 'refinement';       // Final touches and iterations

export interface FashionDesign {
  id: string;
  name: string;
  description: string;
  category: FashionCategory;
  phase: DesignPhase;
  createdAt: Date;

  // Generation history
  iterations: DesignIteration[];

  // Current state
  primaryImage?: string;
  sketchImage?: string;
  animatedVideo?: string;
  multiAngleImages?: string[];

  // Metadata
  tags: string[];
  colors: string[];
  fabrics: string[];
  season?: 'spring' | 'summer' | 'fall' | 'winter' | 'all-season';
  targetMarket?: 'luxury' | 'premium' | 'mid-market' | 'fast-fashion';
}

export type FashionCategory =
  | 'haute-couture'
  | 'ready-to-wear'
  | 'streetwear'
  | 'sportswear'
  | 'evening-wear'
  | 'accessories'
  | 'footwear'
  | 'swimwear'
  | 'lingerie'
  | 'outerwear';

export interface DesignIteration {
  id: string;
  iterationNumber: number;
  timestamp: Date;
  operationType: DesignOperation;

  // Inputs
  prompt: string;
  model: ImageModel;
  referenceImage?: string;
  controlNetImage?: string;
  maskImage?: string;

  // Parameters
  parameters: ImageGenerationParams;

  // Outputs
  resultImages: string[];
  selectedImage?: string;

  // Prompt improvement
  originalPrompt?: string;
  improvedPrompt?: string;
  improvementAgent?: string;

  // Feedback
  userRating?: number;
  notes?: string;
}

export type DesignOperation =
  | 'text-to-image'          // Generate from text description
  | 'sketch-to-image'        // Convert sketch to realistic render
  | 'image-to-image'         // Transform existing image
  | 'inpainting'             // Edit specific areas
  | 'outpainting'            // Extend image boundaries
  | 'clothing-swap'          // Swap garments using Kontext
  | 'context-placement'      // Place in different environments
  | 'style-transfer'         // Apply different artistic styles
  | 'upscale'                // Enhance resolution
  | 'animate'                // Create animated version
  | 'multi-angle'            // Generate multiple viewpoints
  | 'variation';             // Create design variations

export type ImageModel =
  // FLUX Pro Series (via FalAI) - v1.1 endpoints
  | 'flux-pro'                   // FLUX.1 [pro] v1.1 - Premium quality ($0.055)
  | 'flux-pro-ultra'             // FLUX.1 [pro] Ultra - Highest quality, up to 2K ($0.080)
  | 'flux-pro-kontext'           // Context-aware editing for fashion ($0.060)
  | 'flux-pro-redux'             // Style transfer with Pro quality ($0.050)
  // FLUX Dev Series (via FalAI)
  | 'flux-dev'                   // FLUX.1 [dev] - Best quality/cost balance ($0.025)
  | 'flux-dev-image-to-image'    // Image editing with dev model ($0.030)
  | 'flux-dev-redux'             // Style transfer with dev model ($0.035)
  // FLUX Krea Series (via FalAI) - Note: uses /flux-1/ path
  | 'flux-krea'                  // FLUX.1 Krea [dev] - Incredible aesthetics ($0.030)
  | 'flux-krea-dev-redux'        // Creative variations - BEST FOR FASHION ($0.035)
  // FLUX Kontext Series - Context-aware editing
  | 'flux-kontext'               // FLUX.1 Kontext [dev] - BEST FOR SKETCH-TO-IMAGE ($0.040)
  | 'flux-kontext-pro'           // FLUX.1 Kontext [pro] - Premium sketch conversion ($0.060)
  // FLUX Schnell (via FalAI) - Fast generation
  | 'flux-schnell'               // Fastest FLUX (1-4 steps) ($0.003)
  | 'flux-schnell-redux'         // Fast style transfer ($0.010)
  // FLUX Special Features (via FalAI)
  | 'flux-lora'                  // Custom LoRA support ($0.035)
  | 'flux-general'               // ControlNet + LoRA + IP-Adapter (hedsketch for sketches) ($0.040)
  | 'flux-lora-canny'            // FLUX [dev] ControlNet Canny ($0.035)
  | 'flux-control-lora-canny'    // FLUX [dev] Control LoRA Canny ($0.035)
  // Stable Diffusion SDXL Series (via FalAI)
  | 'fast-sdxl'                  // General purpose SDXL ($0.010)
  | 'fast-lightning-sdxl'        // Ultra-fast SDXL variant ($0.008)
  | 'sdxl-controlnet-canny'      // Sketch-to-image with SDXL ($0.015)
  | 'sdxl-controlnet-union'      // Multi-type ControlNet support ($0.018)
  // Stable Diffusion 3.5 Series (via FalAI)
  | 'sd35-large'                 // SD 3.5 high quality ($0.030)
  | 'sd35-turbo'                 // Fast SD 3.5 (1-8 steps) ($0.010)
  // Specialized Models (via FalAI)
  | 'aura-sr'                    // GAN-based 4x upscaling ($0.010)
  | 'ideogram-upscale'           // 2x upscaling with prompt guidance ($0.015)
  | 'qwen-image'                 // High-fidelity image transformation ($0.025)
  // Other Providers
  | 'gemini-imagen'              // Google Imagen via Gemini
  | 'photon-1'                   // Luma Labs Photon
  | 'photon-flash-1';            // Luma Labs Photon Flash

export interface ImageGenerationParams {
  width: number;
  height: number;
  numImages: number;
  guidanceScale?: number;
  seed?: number;
  negativePrompt?: string;

  // FLUX Kontext specific
  contextStrength?: number;
  preserveStructure?: boolean;

  // Style controls
  style?: FashionStyle;
  lighting?: LightingPreset;
  cameraAngle?: CameraAngle;
}

export type FashionStyle =
  | 'editorial'
  | 'catalog'
  | 'runway'
  | 'street-style'
  | 'studio'
  | 'lifestyle'
  | 'artistic'
  | 'minimalist'
  | 'maximalist'
  | 'vintage'
  | 'futuristic';

export type LightingPreset =
  | 'natural-daylight'
  | 'studio-softbox'
  | 'dramatic-rim'
  | 'golden-hour'
  | 'high-key'
  | 'low-key'
  | 'neon'
  | 'backlit'
  | 'three-point';

export type CameraAngle =
  | 'full-body-front'
  | 'full-body-back'
  | 'full-body-side'
  | 'three-quarter'
  | 'detail-closeup'
  | 'aerial-top-down'
  | 'low-angle'
  | 'high-angle'
  | 'dutch-angle';

export interface MoodBoardItem {
  id: string;
  type: 'image' | 'color' | 'texture' | 'inspiration' | 'reference';
  url?: string;
  color?: string;
  description?: string;
  tags: string[];
}

export interface FashionStudioSettings {
  defaultModel: ImageModel;
  defaultStyle: FashionStyle;
  autoSaveEnabled: boolean;
  promptImprovementEnabled: boolean;
  nicheAgentIntegration: boolean;

  // Model preferences
  preferredModels: {
    textToImage: ImageModel;
    sketchToImage: ImageModel;
    contextEditing: ImageModel;
    upscaling: ImageModel;
    imageVariation?: ImageModel;
    sketchToImageFast?: ImageModel;
    sketchToImageLegacy?: ImageModel;
  };

  // Quality settings
  preferredResolution: '1024x1024' | '1024x1536' | '1536x1024' | '2048x2048';
  generationQuality: 'fast' | 'balanced' | 'quality';

  // Integration settings
  enableNicheAgents: boolean;
  enablePromptIteration: boolean;
  autoImprovePrompts: boolean;
}

// === API Request/Response Types ===

export interface GenerateFashionImageRequest {
  sessionId: string;
  designId: string;
  operation: DesignOperation;
  model: ImageModel;
  prompt: string;
  userId?: string;  // User ID for asset persistence

  // Optional images
  referenceImage?: string;
  sketchImage?: string;
  maskImage?: string;
  controlNetImage?: string;

  // Parameters
  parameters: ImageGenerationParams;

  // Prompt improvement
  improvePrompt?: boolean;
  nicheAgent?: string;
}

export interface GenerateFashionImageResponse {
  iterationId: string;
  images: {
    url: string;
    width: number;
    height: number;
    seed: number;
  }[];

  // Prompt improvement details
  originalPrompt?: string;
  improvedPrompt?: string;
  improvementReasoning?: string;

  // Generation metadata
  model: string;
  generationTime: number;
  cost?: number;
}

export interface CreateDesignCollectionRequest {
  sessionId: string;
  collectionName: string;
  collectionTheme: string;
  designIds: string[];

  // Collection parameters
  coherenceLevel: 'loose' | 'moderate' | 'strict';
  generateLookbook?: boolean;
  generateTechPacks?: boolean;
}

export interface CreateDesignCollectionResponse {
  collectionId: string;
  lookbookUrl?: string;
  techPackUrls?: string[];
  collectionImages: string[];
}

export interface ImproveDesignPromptRequest {
  prompt: string;
  category: FashionCategory;
  style: FashionStyle;
  operation: DesignOperation;
  referenceImages?: string[];

  // Agent selection
  useNicheAgent?: boolean;
  nicheAgentType?: string;
}

export interface ImproveDesignPromptResponse {
  originalPrompt: string;
  improvedPrompt: string;
  improvements: string[];
  reasoning: string;
  suggestedTags: string[];
  suggestedColors: string[];
  agentUsed: string;
}

export interface AnimateDesignRequest {
  designId: string;
  imageUrl: string;
  animationType: 'catwalk' | 'spin' | 'fabric-flow' | 'zoom' | 'reveal';
  duration: number;
  model: 'luma' | 'runway' | 'pika';
}

export interface AnimateDesignResponse {
  videoUrl: string;
  duration: number;
  thumbnailUrl: string;
}

export interface Generate360ViewRequest {
  designId: string;
  imageUrl: string;
  numAngles: 8 | 12 | 16 | 24;
  includeAnimation?: boolean;
}

export interface Generate360ViewResponse {
  angleImages: {
    angle: number;
    imageUrl: string;
  }[];
  animatedSpinUrl?: string;
}

// === Session Management ===

export interface SaveFashionSessionRequest {
  session: FashionDesignSession;
}

export interface LoadFashionSessionRequest {
  sessionId: string;
}

export interface ExportFashionCollectionRequest {
  sessionId: string;
  format: 'pdf' | 'zip' | 'json';
  includeMoodBoard: boolean;
  includeTechSpecs: boolean;
}

export interface ExportFashionCollectionResponse {
  downloadUrl: string;
  format: string;
  fileSize: number;
}

// === Virtual Try-On Types ===

export type VirtualTryOnModel = 'idm-vton' | 'fashn-v16' | 'fashn-v15' | 'cat-vton' | 'leffa' | 'kling-kolors-v15';

export type GarmentCategory = 'auto' | 'tops' | 'bottoms' | 'one-pieces';
export type TryOnMode = 'performance' | 'balanced' | 'quality';
export type GarmentPhotoType = 'auto' | 'model' | 'flat-lay';
export type ModerationLevel = 'none' | 'permissive' | 'conservative';

// CAT-VTON garment types
export type CATVTONGarmentType = 'upper' | 'lower' | 'overall' | 'inner' | 'outer';

// Leffa garment types
export type LeffaGarmentType = 'upper_body' | 'lower_body' | 'dresses';

export interface VirtualTryOnRequest {
  sessionId: string;
  userId?: string; // For asset persistence
  model: VirtualTryOnModel;
  humanImage: string; // URL or base64
  garmentImage: string; // URL or base64

  // IDM-VTON specific
  garmentDescription?: string; // Required for IDM-VTON
  numInferenceSteps?: number; // Default: 30 for IDM-VTON, CAT-VTON, Leffa

  // FASHN specific (V1.5 and V1.6)
  category?: GarmentCategory; // Default: 'auto'
  mode?: TryOnMode; // Default: 'balanced'
  garmentPhotoType?: GarmentPhotoType; // Default: 'auto'
  moderationLevel?: ModerationLevel; // Default: 'permissive'
  numSamples?: number; // 1-4, default: 1
  outputFormat?: 'png' | 'jpeg'; // Default: 'png'
  segmentationFree?: boolean; // Default: true

  // CAT-VTON specific
  catGarmentType?: CATVTONGarmentType; // Default: 'upper'
  guidanceScale?: number; // Range: 0-20, default: 2.5 (CAT-VTON and Leffa)

  // Leffa specific
  leffaGarmentType?: LeffaGarmentType; // Default: 'upper_body'
  enableSafetyChecker?: boolean; // Default: true

  // Common
  seed?: number;
}

export interface VirtualTryOnResponse {
  model: VirtualTryOnModel;
  images: GeneratedTryOnImage[];
  mask?: string; // IDM-VTON returns segmentation mask
  hasNsfwConcepts?: boolean; // Leffa returns NSFW detection result
  metadata: {
    resolution: string;
    generationTime?: number;
    cost?: number;
    [key: string]: any;
  };
}

export interface GeneratedTryOnImage {
  url: string;
  contentType: string;
  width: number;
  height: number;
}

// Virtual Try-On Model Metadata
export const VIRTUAL_TRYON_MODELS = {
  'idm-vton': {
    name: 'IDM-VTON',
    displayName: 'IDM-VTON (Best Quality)',
    description: 'ECCV 2024 model with superior garment detail preservation',
    resolution: '1024x1024',
    features: ['Segmentation mask', 'Authentic in-the-wild results', 'High-fidelity details'],
    requiresDescription: true,
    supportsFlatLay: false,
    returnsMask: true,
    pricing: 'Contact fal.ai',
  },
  'fashn-v16': {
    name: 'FASHN V1.6',
    displayName: 'FASHN V1.6 (Recommended)',
    description: 'Commercial-grade virtual try-on with flat-lay support',
    resolution: '864x1296',
    features: ['Flat-lay support', 'Multiple quality modes', 'Content moderation', 'Pattern accuracy'],
    requiresDescription: false,
    supportsFlatLay: true,
    returnsMask: false,
    pricing: '$0.075/generation',
  },
  'fashn-v15': {
    name: 'FASHN V1.5',
    displayName: 'FASHN V1.5',
    description: 'Previous version of FASHN with reliable performance',
    resolution: '864x1296',
    features: ['Flat-lay support', 'Quality modes', 'Content moderation'],
    requiresDescription: false,
    supportsFlatLay: true,
    returnsMask: false,
    pricing: '$0.075/generation',
  },
  'cat-vton': {
    name: 'CAT-VTON',
    displayName: 'CAT-VTON',
    description: 'Versatile model with multiple garment type support',
    resolution: 'Variable',
    features: ['Multiple garment types', 'Inner/outer wear support', 'Guidance scale control'],
    requiresDescription: false,
    supportsFlatLay: false,
    returnsMask: false,
    pricing: 'Contact fal.ai',
  },
  'leffa': {
    name: 'Leffa',
    displayName: 'Leffa (Safe)',
    description: 'Virtual try-on with built-in safety checker',
    resolution: 'Variable',
    features: ['Safety checker', 'NSFW detection', 'Multiple garment types', 'Guidance control'],
    requiresDescription: false,
    supportsFlatLay: false,
    returnsMask: false,
    pricing: 'Contact fal.ai',
  },
  'kling-kolors-v15': {
    name: 'Kling Kolors V1.5',
    displayName: 'Kling Kolors V1.5 (Simple)',
    description: 'Streamlined virtual try-on with minimal configuration',
    resolution: 'Variable',
    features: ['Simple setup', 'Fast processing', 'Auto-detection'],
    requiresDescription: false,
    supportsFlatLay: false,
    returnsMask: false,
    pricing: 'Contact fal.ai',
  },
} as const;

// === Utility Types ===

export interface PromptTemplate {
  id: string;
  name: string;
  category: FashionCategory;
  template: string;
  placeholders: string[];
  exampleOutput: string;
}

export const FASHION_PROMPT_TEMPLATES: PromptTemplate[] = [
  {
    id: 'haute-couture-editorial',
    name: 'Haute Couture Editorial',
    category: 'haute-couture',
    template: 'High fashion editorial photograph of {garment_description}, {fabric_details}, {styling_notes}, professional studio lighting, dramatic composition, haute couture aesthetic, shot on Hasselblad, vogue style',
    placeholders: ['garment_description', 'fabric_details', 'styling_notes'],
    exampleOutput: 'High fashion editorial photograph of flowing silk evening gown, iridescent pearl fabric with hand-beaded details, minimal jewelry with architectural shoulders, professional studio lighting, dramatic composition, haute couture aesthetic, shot on Hasselblad, vogue style'
  },
  {
    id: 'streetwear-lifestyle',
    name: 'Streetwear Lifestyle',
    category: 'streetwear',
    template: 'Urban streetwear photography of {garment_description}, {styling_details}, shot in {location}, natural lighting, candid pose, street style aesthetic, contemporary fashion, photorealistic',
    placeholders: ['garment_description', 'styling_details', 'location'],
    exampleOutput: 'Urban streetwear photography of oversized hoodie with graphic print, layered with cargo pants and chunky sneakers, shot in Tokyo street at sunset, natural lighting, candid pose, street style aesthetic, contemporary fashion, photorealistic'
  },
  {
    id: 'catalog-clean',
    name: 'Catalog - Clean',
    category: 'ready-to-wear',
    template: 'Clean product catalog photograph of {garment_description}, {color} {fabric}, neutral grey background, even soft lighting, centered composition, commercial fashion photography, sharp details, e-commerce ready',
    placeholders: ['garment_description', 'color', 'fabric'],
    exampleOutput: 'Clean product catalog photograph of classic blazer, navy blue wool blend, neutral grey background, even soft lighting, centered composition, commercial fashion photography, sharp details, e-commerce ready'
  }
];
